import { ItemDefinition } from '../types/inventory.types';
import { useSpellCatalogStore } from '../store/spellCatalogueSlice';

/**
 * True if the item can be **used** usefully in the current state. Covers
 * the grimoire case: an item whose `unlockSpell` effect targets an
 * already-unlocked spell is useless to use (a spell can only be learned
 * once). Consulted by `InventoryService.useConsumable`.
 *
 * Distinct from `isSinglePurchaseItem`, which controls shop availability:
 * a shop may decide to sell the item only once regardless of what the
 * player does with it afterwards.
 */
export function isItemUseful(def: ItemDefinition): boolean {
  if (!def.effects) {
    return true;
  }
  const catalog = useSpellCatalogStore.getState();
  for (const eff of def.effects) {
    if (eff.handler === 'unlockSpell') {
      const spellId = eff.params.spellId as string;
      if (spellId && catalog.isUnlocked(spellId)) {
        return false;
      }
    }
  }
  return true;
}

/**
 * List of `spellId`s an item would unlock if used. Empty when the item has
 * no `unlockSpell` effect (= not a grimoire). Used shop-side
 * (cf. `useShopPurchasesStore`) to limit purchase to once per target spell.
 */
export function getUnlockedSpellTargets(def: ItemDefinition): string[] {
  if (!def.effects) {
    return [];
  }
  const targets: string[] = [];
  for (const eff of def.effects) {
    if (eff.handler === 'unlockSpell') {
      const spellId = eff.params.spellId as string | undefined;
      if (spellId) {
        targets.push(spellId);
      }
    }
  }
  return targets;
}

/**
 * True if the item is "single-purchase" shop-side — each target spell can
 * be bought only **once** in the whole save (cf. `useShopPurchasesStore`).
 * Detected by the presence of an `unlockSpell` effect (grimoires are the
 * only single-purchase items at launch).
 */
export function isSinglePurchaseItem(def: ItemDefinition): boolean {
  return getUnlockedSpellTargets(def).length > 0;
}
